import { noop } from 'lodash';

import { type OnProgressParameter, type UploadFileParams, type UploadFileResponse } from './types';
import { UploadStallWatchdog } from './uploadStallWatchdog';
import { UPLOAD_STALL_TIMEOUT, UPLOAD_STALL_TIMEOUT_MS, UploadStatusError, USER_ABORTED_UPLOAD } from './utils';

const HTTP_SUCCESS_STATUS_MIN = 200;
const HTTP_SUCCESS_STATUS_EXCLUSIVE_MAX = 300;

const isSuccessStatus = (status: number) =>
  status >= HTTP_SUCCESS_STATUS_MIN && status < HTTP_SUCCESS_STATUS_EXCLUSIVE_MAX;

export const uploadFile = async ({
  file,
  url,
  contentType,
  onProgress,
  onStart = noop,
  returnTag,
  stallTimeoutMs = UPLOAD_STALL_TIMEOUT_MS,
}: UploadFileParams): Promise<UploadFileResponse> => {
  const xhr: XMLHttpRequest = new XMLHttpRequest();
  const stallWatchdog = new UploadStallWatchdog(stallTimeoutMs, () => xhr.abort());

  const abort = () => {
    stallWatchdog.dispose();
    xhr.abort();
  };

  return new Promise((resolve, reject) => {
    const rejectWithCleanup = (error: unknown) => {
      stallWatchdog.dispose();
      reject(error);
    };

    xhr.open('PUT', url, true);
    xhr.upload.onprogress = (evt) => {
      const param: OnProgressParameter = { abort };

      if (evt.lengthComputable) {
        param.progress = evt.loaded;
        stallWatchdog.recordProgress(evt.loaded);
      }

      onProgress?.(param);
    };
    xhr.onerror = () => rejectWithCleanup(new UploadStatusError(xhr.status));
    xhr.onabort = () => {
      if (stallWatchdog.getAbortReason() === 'stall') {
        rejectWithCleanup(new Error(UPLOAD_STALL_TIMEOUT));
        return;
      }

      rejectWithCleanup(new Error(USER_ABORTED_UPLOAD));
    };
    xhr.onload = () => {
      stallWatchdog.dispose();

      if (!isSuccessStatus(xhr.status)) {
        reject(new UploadStatusError(xhr.status));
        return;
      }

      if (returnTag) {
        const xhrResponseHeaderETag = xhr.getResponseHeader('ETag');

        /**
         * We wnat to be sure there is an ETag header if we're requiring one. We had issues where certain users we're getting a successful upload
         * but no `ETag` header was present. throwing here will force the upload to retry that part again
         *
         * @see https://air-labs-team.slack.com/archives/CKE8SK3M1/p1667488801846359
         */
        if (xhrResponseHeaderETag) {
          resolve({
            ETag: xhrResponseHeaderETag,
          });
        } else {
          reject(new Error('Missing ETag for upload'));
        }
      } else {
        resolve({});
      }
    };
    xhr.setRequestHeader('Content-Type', contentType);
    stallWatchdog.start();
    xhr.send(file);
    onStart(xhr);
  });
};
